import { ErrorCode, NativeBridgeError } from "./types"
import { addLogDebug } from "../logger"

/**
 * Mock implementation of NativeBridge for web development
 * Simulates iOS native bridge behavior in browser
 */
interface MockRequest {
  id: string
  method: string
  params?: unknown
}

interface MockMeasurement {
  id: string
  timestamp: number
  value: number
  valueType: string
}

export class MockNativeBridge {
  private messageHandlers = new Map<string, (request: MockRequest) => Promise<unknown>>()
  private eventListeners = new Map<string, Set<(data: unknown) => void>>()
  private subscriptionCounter = 0
  private subscriptions = new Map<string, { event: string; callback: (data: unknown) => void }>()

  // Mock state
  private mockState = {
    health: {
      isConnected: false,
      isAutoSyncEnabled: false,
      authorizationStatus: "notDetermined" as "notDetermined" | "denied" | "authorized",
      workouts: this.generateMockWorkouts(),
    },
    lactate: {
      isConnected: false,
      deviceId: null as string | null,
      measurements: [] as MockMeasurement[],
      devices: [
        {
          address: "MOCK_EN605_01",
          name: "TE 605-01",
          family: "EN605" as "EN605" | "EN011",
          syncOnDiscovery: true,
          addedAtMs: 0,
          lastSeenMs: 0,
        },
        {
          address: "MOCK_EN011_02",
          name: "Eaglenos-001",
          family: "EN011" as "EN605" | "EN011",
          syncOnDiscovery: false,
          addedAtMs: 0,
          lastSeenMs: 0,
        },
      ],
      activeAddress: "MOCK_EN605_01" as string | null,
    },
    system: {
      appVersion: "2.0.1",
      osVersion: "17.0",
      deviceModel: "iPhone 15 Pro",
      buildNumber: "100",
    },
  }

  constructor() {
    this.registerMockHandlers()
    addLogDebug("MockNativeBridge", "Initialized for web development")
  }

  /**
   * Send request to native bridge
   */
  async request<TParams = unknown, TResponse = unknown>(
    method: string,
    params?: TParams,
    _timeout?: number,
  ): Promise<TResponse> {
    const requestId = `mock_${Date.now()}_${Math.random()}`

    addLogDebug("MockNativeBridge", `Request: ${method}`, params)

    // Find handler
    const handler = this.messageHandlers.get(method)
    if (!handler) {
      throw new NativeBridgeError(ErrorCode.METHOD_NOT_FOUND, `Method ${method} not found`)
    }

    try {
      // Simulate network delay
      await new Promise((resolve) => {
        setTimeout(resolve, 100 + Math.random() * 300)
      })

      const response = await handler({ id: requestId, method, params })
      addLogDebug("MockNativeBridge", `Response: ${method}`, response)
      return response as TResponse
    } catch (error) {
      addLogDebug("MockNativeBridge", `Error: ${method}`, error)
      throw error
    }
  }

  /**
   * Subscribe to native events
   */
  async subscribe<TEvent = unknown>(event: string, callback: (data: TEvent) => void): Promise<string> {
    const subscriptionId = `sub_${++this.subscriptionCounter}`

    addLogDebug("MockNativeBridge", `Subscribe to: ${event} (${subscriptionId})`)

    const typedCallback = callback as (data: unknown) => void

    // Store subscription
    this.subscriptions.set(subscriptionId, { event, callback: typedCallback })

    // Add to event listeners
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, new Set())
    }
    this.eventListeners.get(event)!.add(typedCallback)

    // Simulate subscription response
    await new Promise((resolve) => {
      setTimeout(resolve, 50)
    })

    return subscriptionId
  }

  /**
   * Unsubscribe from native events
   */
  async unsubscribe(subscriptionId: string): Promise<void> {
    addLogDebug("MockNativeBridge", `Unsubscribe: ${subscriptionId}`)

    const subscription = this.subscriptions.get(subscriptionId)
    if (!subscription) {
      return
    }

    // Remove from event listeners
    const listeners = this.eventListeners.get(subscription.event)
    if (listeners) {
      listeners.delete(subscription.callback)
      if (listeners.size === 0) {
        this.eventListeners.delete(subscription.event)
      }
    }

    // Remove subscription
    this.subscriptions.delete(subscriptionId)
  }

  /**
   * Emit mock event
   */
  private emitEvent(event: string, data: unknown) {
    const listeners = this.eventListeners.get(event)
    if (listeners) {
      listeners.forEach((callback) => {
        setTimeout(() => callback(data), 0)
      })
    }
  }

  /**
   * Generate mock workouts
   */
  private generateMockWorkouts() {
    const now = new Date()
    const workouts = [
      {
        id: "workout_1",
        type: "running",
        date: new Date(now.getTime() - 1 * 24 * 60 * 60 * 1000).toISOString(),
        duration: 1824, // 30:24
        distance: 5420, // 5.42 km
        isImported: false,
      },
      {
        id: "workout_2",
        type: "cycling",
        date: new Date(now.getTime() - 3 * 24 * 60 * 60 * 1000).toISOString(),
        duration: 3600, // 60 min
        distance: 25000, // 25 km
        isImported: true,
      },
      {
        id: "workout_3",
        type: "swimming",
        date: new Date(now.getTime() - 5 * 24 * 60 * 60 * 1000).toISOString(),
        duration: 2400, // 40 min
        distance: 1500, // 1.5 km
        isImported: false,
      },
      {
        id: "workout_4",
        type: "walking",
        date: new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString(),
        duration: 2700, // 45 min
        distance: 3500, // 3.5 km
        isImported: true,
      },
      {
        id: "workout_5",
        type: "running",
        date: new Date(now.getTime() - 10 * 24 * 60 * 60 * 1000).toISOString(),
        duration: 2100, // 35 min
        distance: 7200, // 7.2 km
        isImported: false,
      },
    ]
    return workouts
  }

  /**
   * Register mock method handlers
   */
  private registerMockHandlers() {
    // Health methods
    this.messageHandlers.set("health.connect", async () => {
      // Simulate connection delay
      await new Promise((resolve) => {
        setTimeout(resolve, 1000)
      })

      // Simulate different authorization scenarios
      if (this.mockState.health.authorizationStatus === "notDetermined") {
        // First time connecting - randomly grant or deny
        const granted = Math.random() > 0.3 // 70% chance of granting
        if (granted) {
          this.mockState.health.authorizationStatus = "authorized"
          this.mockState.health.isConnected = true
          this.emitEvent("health.connected", {})
        } else {
          this.mockState.health.authorizationStatus = "denied"
          this.mockState.health.isConnected = false
          this.emitEvent("health.authorizationRequired", {})
        }
      } else if (this.mockState.health.authorizationStatus === "denied") {
        // User previously denied - they need to go to settings
        this.emitEvent("health.authorizationRequired", {})
      } else {
        // Already authorized - toggle connection
        this.mockState.health.isConnected = !this.mockState.health.isConnected
        if (this.mockState.health.isConnected) {
          this.emitEvent("health.connected", {})
        }
      }

      // Return response based on authorization status
      if (this.mockState.health.authorizationStatus === "denied") {
        return {
          status: "failed",
          error: {
            code: "HEALTH_AUTHORIZATION_DENIED",
            message: "User denied access to HealthKit",
          },
        }
      }

      return {
        status: this.mockState.health.isConnected ? "success" : "failed",
        message: this.mockState.health.isConnected
          ? "Successfully connected to Apple Health"
          : "Disconnected from Apple Health",
      }
    })

    this.messageHandlers.set("health.getConnectionStatus", async () => {
      return {
        connected: this.mockState.health.isConnected,
        available: true,
        authorizationStatus: this.mockState.health.authorizationStatus,
        authorizedTypes: this.mockState.health.authorizationStatus === "authorized" ? ["HKWorkoutTypeIdentifier"] : [],
      }
    })

    this.messageHandlers.set("health.getAutoSyncStatus", async () => {
      return { enabled: this.mockState.health.isAutoSyncEnabled }
    })

    this.messageHandlers.set("health.toggleAutoSync", async (request) => {
      const params = request.params as { enabled?: boolean } | undefined
      const enabled = params?.enabled ?? false
      this.mockState.health.isAutoSyncEnabled = enabled
      return {} // void response
    })

    this.messageHandlers.set("health.getWorkouts", async (request) => {
      const params = request.params as { days?: number } | undefined
      const days = params?.days ?? 30
      const cutoffDate = new Date()
      cutoffDate.setDate(cutoffDate.getDate() - days)

      const filteredWorkouts = this.mockState.health.workouts.filter((workout) => {
        const workoutDate = new Date(workout.date)
        return workoutDate > cutoffDate
      })

      return {
        workouts: filteredWorkouts,
        total: filteredWorkouts.length,
      }
    })

    this.messageHandlers.set("health.exportWorkout", async (request) => {
      const params = request.params as { workoutId?: string } | undefined
      const workoutId = params?.workoutId
      if (!workoutId) {
        throw new NativeBridgeError(ErrorCode.INVALID_PARAMS, "Missing workoutId")
      }

      // Simulate export delay
      await new Promise((resolve) => {
        setTimeout(resolve, 500)
      })

      // Generate dummy FIT data
      const fitData =
        "UEsDBAoAAAAAAIdO4kAAAAAAAAAAAAAAAAAJAAAAbWV0YS5qc29uUEsBAj8ACgAAAAAAh07iQAAAAAAAAAAAAAAAAAkAJAAAAAAAAAAQgO1BAAAAABtldGEuanNvbgoAIAAAAAAAAQAYAACAPtXesQEAgD7V3rEBAIA+1d6xAQBQSwUGAAAAAAEAAQBbAAAAJwAAAAAA"

      return {
        data: fitData,
        filename: `workout_${workoutId}_${Date.now()}.fit`,
      }
    })

    this.messageHandlers.set("health.markWorkoutImported", async (request) => {
      const params = request.params as { workoutId?: string } | undefined
      const workoutId = params?.workoutId
      if (!workoutId) {
        throw new NativeBridgeError(ErrorCode.INVALID_PARAMS, "Missing workoutId")
      }

      const workout = this.mockState.health.workouts.find((w) => w.id === workoutId)
      if (workout) {
        workout.isImported = true
      }

      return { imported: true }
    })

    // System methods
    this.messageHandlers.set("system.getInfo", async () => {
      return this.mockState.system
    })

    this.messageHandlers.set("system.checkSupport", async () => {
      return {
        bluetooth: true,
        lactate: true,
        lactateV2: true,
        workout: true,
        health: true,
        platform: "ios",
      }
    })

    // Lactate methods
    this.messageHandlers.set("lactate.connect", async () => {
      // Simulate connection
      await new Promise((resolve) => {
        setTimeout(resolve, 2000)
      })

      this.mockState.lactate.isConnected = true
      this.mockState.lactate.deviceId = "MOCK_DEVICE_001"

      // Emit connected event
      this.emitEvent("lactate.connected", {
        deviceId: this.mockState.lactate.deviceId,
        deviceName: "Mock Lactate Monitor",
      })

      // Start emitting mock measurements
      this.startMockLactateMeasurements()

      return {
        deviceId: this.mockState.lactate.deviceId,
        deviceName: "Mock Lactate Monitor",
        connected: true,
      }
    })

    this.messageHandlers.set("lactate.disconnect", async () => {
      this.mockState.lactate.isConnected = false
      this.mockState.lactate.deviceId = null

      // Emit disconnected event
      this.emitEvent("lactate.disconnected", {
        deviceId: "MOCK_DEVICE_001",
        reason: "User requested disconnect",
      })

      return { success: true }
    })

    this.messageHandlers.set("lactate.getState", async () => {
      const active = this.mockState.lactate.activeAddress
      return {
        isConnected: this.mockState.lactate.isConnected,
        isPaired: this.mockState.lactate.devices.length > 0,
        isScanning: false,
        devices: this.listMockDevices(),
        activeAddress: active ?? "",
      }
    })

    this.messageHandlers.set("lactate.getMeasurements", async () => {
      return {
        measurements: this.mockState.lactate.measurements,
      }
    })

    // --- Multi-device (lactateV2) mock -------------------------------------
    this.messageHandlers.set("lactate.listDevices", async () => ({
      devices: this.listMockDevices(),
      activeAddress: this.mockState.lactate.activeAddress ?? "",
    }))

    this.messageHandlers.set("lactate.setActiveDevice", async (request) => {
      const address = (request.params as { address?: string } | undefined)?.address ?? ""
      this.mockState.lactate.activeAddress = address
      this.emitEvent("lactate.activeDeviceChanged", { address })
      return { activeAddress: address }
    })

    this.messageHandlers.set("lactate.addDevice", async () => {
      const address = `MOCK_NEW_${Date.now()}`
      const name = "TE 605-new"
      this.mockState.lactate.devices.push({
        address,
        name,
        family: "EN605",
        syncOnDiscovery: true,
        addedAtMs: Date.now(),
        lastSeenMs: Date.now(),
      })
      this.mockState.lactate.activeAddress = address
      this.emitEvent("lactate.devicePaired", { address, name })
      return { deviceId: address, deviceName: name, connected: true }
    })

    this.messageHandlers.set("lactate.forgetDevice", async (request) => {
      const address = (request.params as { address?: string } | undefined)?.address ?? ""
      this.mockState.lactate.devices = this.mockState.lactate.devices.filter((d) => d.address !== address)
      if (this.mockState.lactate.activeAddress === address) {
        this.mockState.lactate.activeAddress = this.mockState.lactate.devices[0]?.address ?? null
      }
      this.emitEvent("lactate.deviceForgotten", { address })
      return { status: "forgotten" }
    })

    this.messageHandlers.set("lactate.setDeviceSyncOnDiscovery", async (request) => {
      const params = request.params as { address?: string; enabled?: boolean } | undefined
      const device = this.mockState.lactate.devices.find((d) => d.address === params?.address)
      if (device) device.syncOnDiscovery = Boolean(params?.enabled)
      return { address: params?.address ?? "", syncOnDiscovery: Boolean(params?.enabled) }
    })

    this.messageHandlers.set("lactate.setDeviceTime", async () => ({ status: "ok" }))
    this.messageHandlers.set("lactate.clearDevice", async () => ({ status: "ok" }))

    // Workout methods
    this.messageHandlers.set("workout.create", async (request) => {
      const params = request.params as { workoutId?: string } | undefined
      const workoutId = params?.workoutId
      return {
        scheduled: true,
        workoutId: workoutId || `mock_workout_${Date.now()}`,
      }
    })
  }

  /** Snapshot the mock registry with computed `isActive`/`online` flags. */
  private listMockDevices() {
    const active = this.mockState.lactate.activeAddress
    return this.mockState.lactate.devices.map((d) => ({
      ...d,
      isActive: d.address === active,
      online: this.mockState.lactate.isConnected && d.address === active,
    }))
  }

  /**
   * Start emitting mock lactate measurements
   */
  private startMockLactateMeasurements() {
    if (!this.mockState.lactate.isConnected) return

    const emitMeasurement = () => {
      if (!this.mockState.lactate.isConnected) return

      const measurement = {
        id: `measurement_${Date.now()}`,
        timestamp: Date.now(),
        value: 1.5 + Math.random() * 3, // Random value between 1.5 and 4.5
        valueType: "Lactate",
      }

      this.mockState.lactate.measurements.push(measurement)

      // Keep only last 20 measurements
      if (this.mockState.lactate.measurements.length > 20) {
        this.mockState.lactate.measurements.shift()
      }

      this.emitEvent("lactate.measurement", { measurement })

      // Schedule next measurement
      setTimeout(emitMeasurement, 3000 + Math.random() * 2000)
    }

    // Start after a short delay
    setTimeout(emitMeasurement, 1000)
  }
}

// Export singleton instance
export const mockNativeBridge = new MockNativeBridge()
